import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { Flp360LoginComponentSettings } from '../component-settings';
import { AppModalDirective } from '../../common/directives/app-modal.directive';
import { AppMessageService } from '../../app-message.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { CacheKey } from '../constants/cachekey.constants';
import { MemberStatus } from '../enums';
import { CommonService } from '../services/common.service';
import { ActiveSessionService } from '../services/active-session.service';
import { CacheService } from '../services/cache.service';
import { NotificationService } from '../services/notification.service';
import { NotificationType } from 'src/app/common/enums/notification-type.enum';
import { Member } from '../interfaces/member.interface';

/**
 * @description this component used to display the account
 * details of user
 * @date 2018-09-20
 * @export
 * @class Flp360LoginComponent
 * @implements {OnInit}
 */
@Component({
    selector: 'app-flp360-login',
    templateUrl: '../templates/template1/views/flp360-login.component.html',
    styleUrls: ['../templates/template1/themes/default/less/new-app-header.less']
})

export class Flp360LoginComponent implements OnInit, OnDestroy {
    flp360LoginComponentSettings: Flp360LoginComponentSettings = new Flp360LoginComponentSettings();
    @ViewChild('flp360InfoModalPopup', { static: false }) public flp360InfoModalPopup: AppModalDirective;
    constructor(
        private commonService: CommonService,
        private activeSessionService: ActiveSessionService,
        private cacheService: CacheService,
        private appMessageService: AppMessageService,
        private translatePipe: TranslateService,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        this.loadDefaultSettings();
        this.getFlp360LoginDetails();
    }
    /**
     * @description this method will used to load default settings of userinfo
     * related to the account
     * @private
     * @memberof Flp360LoginComponent
     */
    private loadDefaultSettings(): void {
        this.flp360LoginComponentSettings.isoCountryCode = this.cacheService.getCookieValue(CacheKey.countryCode);
        this.flp360LoginComponentSettings.languageCode = this.cacheService.getCookieValue(CacheKey.languageCode);
        this.flp360LoginComponentSettings.userInfo = this.activeSessionService.getUserInfo();
        this.flp360LoginComponentSettings.store = this.cacheService.get(CacheKey.StoreConfiguration);
        this.getFlp360LoginSubject();
    }

  /**
   * @description
   * @date 2019-09-11
   * @private
   * @memberof Flp360LoginComponent
   */
    private getFlp360LoginSubject(): void {
        this.flp360LoginComponentSettings.flp360LoginSubscription = this.appMessageService.getFlp360LoginSubject()
            .subscribe((isFlp360LoginClicked: boolean) => {
                if (isFlp360LoginClicked) {
                    this.flp360Login();
                }
            });
    }

/**
 *
 *
 * @memberof Flp360LoginComponent
 */
getFlp360LoginDetails() {
    // Check if FLP360 featureEnabled
    if (environment.flp360FeatureEnabled) {
        const fboId = this.activeSessionService.getDistributorId();
        if (fboId) {
            this.commonService.verifyFlp360Access(fboId)
                .subscribe(response => {
                    if (response) {
                        this.flp360LoginComponentSettings.verifiedFlp360UserInfo = response;
                    }
                });
        }
    } else {
        window.open(environment.flp360LoginUrl, '_blank');
    }
}

  /**
   * @description Navigate to flp360 login
   * @memberof Flp360LoginComponent
   */
flp360Login(): void {
        if (this.flp360LoginComponentSettings.verifiedFlp360UserInfo.flp360Enabled &&
            this.flp360LoginComponentSettings.verifiedFlp360UserInfo.memberStatusId === MemberStatus.ACTIVE) {
            const googleValidationDone: boolean = (this.flp360LoginComponentSettings.verifiedFlp360UserInfo.googleEmailId
                && this.flp360LoginComponentSettings.verifiedFlp360UserInfo.userAvailableInFlp360) ||
                 (!this.flp360LoginComponentSettings.verifiedFlp360UserInfo.googleEmailId);
            if (googleValidationDone) {
                window.open(environment.flp360LoginUrl, '_blank');
            } else {
                // Show first time login message
                this.flp360InfoModalPopup.show();
            }
        } else {
            if (this.flp360LoginComponentSettings.verifiedFlp360UserInfo.flp360StatusId ===
                this.flp360LoginComponentSettings.flp360StatusEnum.SUSPENDED) {
                this.translatePipe.get('flp360.' + 'Flp360AccountSuspended').subscribe((value) => {
                    this.notificationService.createNotification('', value, NotificationType.ERROR);
                });
            } else {
                this.flp360SignUp();
            }
        }
    }

    /**
     * @description this method will used for navigate to flp360 signup page
     * flp 360 signup
     * @memberof Flp360LoginComponent
     */
    flp360SignUp(): void {
        // Verify if Flp360 enabled
        const fboId = this.activeSessionService.getDistributorId();
        if (fboId) {
            const userInfo: Member = this.cacheService.get(CacheKey.UserInfo);
            if (userInfo && userInfo.email && userInfo.email.trim()) {
                const redirectUrl = environment.flp360TitanSignupUrl +
                    '?firstName=' + userInfo.firstName + '&lastName=' + userInfo.lastName +
                    '&distributorId=' + userInfo.distributorId + '&emailId=' + userInfo.email;
                window.open(redirectUrl, '_blank');
            } else {
                // Show email update message
                const msg: any = this.translatePipe.get('flp360.' + 'UpdateEmail');
                this.notificationService.createNotification('', msg.value, NotificationType.ERROR);
            }
        }
    }

    /**
     * @description this method will logout and navigate to
     * flp 360 login page
     * @memberof Flp360LoginComponent
     */
    logoutAndNavigateToFLP360(): void {
        this.cacheService.set(CacheKey.Flp360LoginClicked, true);
        this.appMessageService.setNotifyLoginStatus(false);
    }

   /**
    * To avoid MemoryLeaks need to unsubscribe.
    */
    ngOnDestroy() {
        if (this.flp360LoginComponentSettings.flp360LoginSubscription) {
            this.flp360LoginComponentSettings.flp360LoginSubscription.unsubscribe();
        }
    }
}
